// Fuzzy search utilities for command palette
import React from 'react';

// Calculate Levenshtein distance between two strings
export function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  
  // Initialize first column
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  
  // Initialize first row
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  
  // Fill in the rest of the matrix
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix[b.length][a.length];
}

// Calculate similarity score (0-1) based on Levenshtein distance
export function similarityScore(a: string, b: string): number {
  const distance = levenshteinDistance(a.toLowerCase(), b.toLowerCase());
  const maxLength = Math.max(a.length, b.length);
  return maxLength === 0 ? 1 : 1 - (distance / maxLength);
}

// Fuzzy match with tolerance
export function fuzzyMatch(query: string, target: string, tolerance: number = 0.3): boolean {
  return similarityScore(query, target) >= (1 - tolerance);
}

// Highlight matching parts in text (returns safe HTML)
export function highlightMatch(text: string, query: string): string {
  if (!query) return text;
  
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
}

// Safe highlight match that returns React elements instead of HTML string
export function safeHighlightMatch(text: string, query: string): React.ReactNode[] {
  if (!query) return [text];
  
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => {
    // Every odd index is a match
    if (index % 2 === 1 && regex.test(part)) {
      return <mark key={index}>{part}</mark>;
    }
    return part;
  });
}

// Escape special regex characters
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Common medical abbreviations and their expansions
const MEDICAL_ABBREVIATIONS: Record<string, string[]> = {
  'bp': ['blood pressure', 'biomarker panel'],
  'hr': ['heart rate', 'human resources'],
  'iv': ['intravenous', 'iv therapy'],
  'hbot': ['hyperbaric oxygen therapy'],
  'pet': ['physical therapy', 'positron emission tomography'],
  'fir': ['far infrared', 'fir therapy'],
  'pmx': ['preventive medicine', 'pmx reports'],
  'lab': ['laboratory', 'lab results', 'test results'],
  'rx': ['prescription', 'medication'],
  'dx': ['diagnosis', 'diagnostic'],
  'hx': ['history', 'medical history'],
  'pt': ['patient', 'physical therapy'],
  'appt': ['appointment', 'appointments'],
  'consult': ['consultation', 'consultations'],
  'meds': ['medications', 'medicines'],
  'supp': ['supplement', 'supplements'],
  'bio': ['biomarker', 'biomarkers'],
};

// Expand abbreviations in query
export function expandAbbreviations(query: string): string[] {
  const words = query.toLowerCase().split(/\s+/);
  const expansions: string[] = [query];
  
  words.forEach(word => {
    if (MEDICAL_ABBREVIATIONS[word]) {
      MEDICAL_ABBREVIATIONS[word].forEach(expansion => {
        expansions.push(query.replace(new RegExp(`\\b${word}\\b`, 'i'), expansion));
      });
    }
  });
  
  return [...new Set(expansions)];
}

// Common typos and corrections
const TYPO_CORRECTIONS: Record<string, string> = {
  'appoitment': 'appointment',
  'appointmnet': 'appointment',
  'clincal': 'clinical',
  'clinial': 'clinical',
  'threhold': 'threshold',
  'threshhold': 'threshold',
  'patinet': 'patient',
  'pateint': 'patient',
  'medcine': 'medicine',
  'medicne': 'medicine',
  'labatory': 'laboratory',
  'labratory': 'laboratory',
  'biomarker': 'biomarker',
  'biomaker': 'biomarker',
  'questionare': 'questionnaire',
  'questionairre': 'questionnaire',
  'supliment': 'supplement',
  'suppliment': 'supplement',
};

// Correct common typos
export function correctTypos(query: string): string {
  let corrected = query.toLowerCase();
  
  Object.entries(TYPO_CORRECTIONS).forEach(([typo, correction]) => {
    corrected = corrected.replace(new RegExp(`\\b${typo}\\b`, 'g'), correction);
  });
  
  return corrected;
}

// Generate search variations
export function generateSearchVariations(query: string): string[] {
  const variations: string[] = [];
  
  // Original query
  variations.push(query);
  
  // Corrected typos
  const corrected = correctTypos(query);
  if (corrected !== query.toLowerCase()) {
    variations.push(corrected);
  }
  
  // Expanded abbreviations
  const expansions = expandAbbreviations(query);
  variations.push(...expansions);
  
  // Remove duplicates
  return [...new Set(variations)];
}

// Category display names
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  'page': 'Pages',
  'client': 'Client Pages',
  'tab': 'Tabs',
  'section': 'Sections',
};

// Sort search results by relevance
export function sortByRelevance<T extends { score: number; category: string }>(
  results: T[]
): T[] {
  return results.sort((a, b) => {
    // First sort by score
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    
    // Then by category priority
    const categoryPriority = { page: 1, section: 2, tab: 3, client: 4 };
    const aPriority = categoryPriority[a.category as keyof typeof categoryPriority] || 5;
    const bPriority = categoryPriority[b.category as keyof typeof categoryPriority] || 5;
    
    return aPriority - bPriority;
  });
}